import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Trash2, Download, Send, Plus, X, Sparkles, AlertCircle } from "lucide-react";

export default function AdminContracts() {
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showGenerate, setShowGenerate] = useState(null); // template object

  const load = async () => {
    try {
      const [t, c] = await Promise.all([
        api.get("/admin/contracts/templates"),
        api.get("/admin/contracts"),
      ]);
      setTemplates(t.data || []);
      setContracts(c.data || []);
    } catch { toast.error("Daten konnten nicht geladen werden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDeleteTemplate = async (id) => {
    if (!window.confirm("Vorlage wirklich löschen? Bereits generierte Verträge bleiben erhalten.")) return;
    try { await api.delete(`/admin/contracts/templates/${id}`); toast.success("Gelöscht"); load(); }
    catch { toast.error("Fehler"); }
  };

  const onDeleteContract = async (id) => {
    if (!window.confirm("Diesen generierten Vertrag wirklich löschen?")) return;
    try { await api.delete(`/admin/contracts/${id}`); toast.success("Gelöscht"); load(); }
    catch { toast.error("Fehler"); }
  };

  const onSendContract = async (id) => {
    if (!window.confirm("PDF jetzt per Mail an den Kunden senden?")) return;
    try { await api.post(`/admin/contracts/${id}/send`); toast.success("E-Mail wird versendet"); setTimeout(load, 2000); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const onDownloadGenerated = async (id, kind, label) => {
    try {
      const res = await api.get(`/admin/contracts/${id}/file/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  return (
    <div data-testid="admin-contracts">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl text-[#1A2433] mb-1">Verträge</h1>
          <p className="text-slate-600 text-sm">Vorlagen verwalten und Kundenverträge mit Auto-Fill generieren.</p>
        </div>
        <button onClick={() => setShowUpload(true)} className="btn-primary flex-shrink-0" data-testid="upload-template-btn">
          <Upload className="w-4 h-4 mr-2" /> Vorlage hochladen
        </button>
      </header>

      {/* Templates */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <h2 className="font-heading text-lg text-[#1A2433] mb-4">Vorlagen ({templates.length})</h2>
        {loading ? <p className="text-slate-500 text-sm">Lade…</p> : templates.length === 0 ? (
          <div className="text-sm text-slate-500 italic">
            Noch keine Vorlagen hochgeladen. Laden Sie eine `.docx`-Datei mit Platzhaltern wie <code className="bg-slate-100 px-1 rounded">[Vorname Nachname / Firma]</code> hoch.
          </div>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t.id} className="border border-slate-200 rounded-lg p-4" data-testid={`template-${t.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#1A2433]">{t.name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {t.filename} · {(t.size / 1024).toFixed(0)} KB · {new Date(t.created_at).toLocaleDateString("de-CH")}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(t.placeholders || []).map((p) => (
                        <span key={p} className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 rounded font-mono">
                          [{p}]
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => setShowGenerate(t)} className="bg-[#991B1B] hover:bg-[#7c1414] text-white text-xs font-medium px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5" data-testid={`generate-btn-${t.id}`}>
                      <Sparkles className="w-3.5 h-3.5" /> Vertrag generieren
                    </button>
                    <button onClick={() => onDeleteTemplate(t.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-slate-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Generated contracts */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <h2 className="font-heading text-lg text-[#1A2433] mb-4">Generierte Verträge ({contracts.length})</h2>
        {contracts.length === 0 ? (
          <div className="text-sm text-slate-500 italic">Noch keine Verträge erzeugt.</div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3">Kunde</th>
                  <th className="px-4 py-3">Vorlage</th>
                  <th className="px-4 py-3">Datum</th>
                  <th className="px-4 py-3">Versendet</th>
                  <th className="px-4 py-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`contract-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#1A2433]">
                        {c.user?.first_name} {c.user?.last_name}
                      </div>
                      <div className="text-xs text-slate-500">{c.user?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.template_name}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{new Date(c.created_at).toLocaleString("de-CH")}</td>
                    <td className="px-4 py-3 text-xs">
                      {c.emailed_at
                        ? <span className="text-emerald-700 font-medium">✓ {new Date(c.emailed_at).toLocaleDateString("de-CH")}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => onDownloadGenerated(c.id, "pdf", c.template_name)} className="p-1.5 text-slate-600 hover:text-[#991B1B] hover:bg-slate-50 rounded" title="PDF herunterladen" data-testid={`download-pdf-${c.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => onDownloadGenerated(c.id, "docx", c.template_name)} className="p-1.5 text-slate-600 hover:text-[#991B1B] hover:bg-slate-50 rounded text-[11px] font-bold" title="DOCX herunterladen">
                          DOC
                        </button>
                        <button onClick={() => onSendContract(c.id)} className="p-1.5 text-slate-600 hover:text-[#991B1B] hover:bg-slate-50 rounded" title="An Kunde mailen" data-testid={`send-${c.id}`}>
                          <Send className="w-4 h-4" />
                        </button>
                        <button onClick={() => onDeleteContract(c.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-slate-50 rounded" title="Löschen">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showUpload && <UploadDialog onClose={() => setShowUpload(false)} onDone={load} />}
      {showGenerate && <GenerateDialog template={showGenerate} onClose={() => setShowGenerate(null)} onDone={load} />}
    </div>
  );
}

function UploadDialog({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || !file) { toast.error("Name und Datei erforderlich"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name.trim());
    setBusy(true);
    try {
      const { data } = await api.post("/admin/contracts/templates/upload", fd);
      toast.success(`Vorlage hochgeladen — ${data.placeholders.length} Platzhalter erkannt`);
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog onClose={onClose} title="Vorlage hochladen">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#1A2433] mb-1">Name *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Beratungsvertrag Unternehmensgründung"
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#991B1B]/30" data-testid="template-name-input" />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#1A2433] mb-1">DOCX-Datei *</label>
          <input type="file" accept=".docx" onChange={(e) => setFile(e.target.files?.[0])}
            className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#1A2433] file:text-white file:cursor-pointer" data-testid="template-file-input" />
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>Verwenden Sie Platzhalter in eckigen Klammern, z.B. <code className="bg-white px-1 rounded">[Vorname Nachname / Firma]</code>, <code className="bg-white px-1 rounded">[Adresse]</code>, <code className="bg-white px-1 rounded">[Betrag]</code>. Diese werden beim Generieren automatisch ersetzt.</div>
        </div>
      </div>
      <DialogFooter>
        <button onClick={onClose} className="btn-secondary">Abbrechen</button>
        <button onClick={submit} disabled={busy} className="btn-primary" data-testid="upload-submit-btn">
          {busy ? "Lädt…" : "Hochladen"}
        </button>
      </DialogFooter>
    </Dialog>
  );
}

function GenerateDialog({ template, onClose, onDone }) {
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [overrides, setOverrides] = useState({});
  const [sendEmail, setSendEmail] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then(({ data }) => {
      setUsers(data.filter((u) => u.role !== "admin"));
    }).catch(() => {});
  }, []);

  const submit = async () => {
    if (!userId) { toast.error("Bitte Kunde wählen"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/contracts/generate", {
        template_id: template.id,
        user_id: userId,
        overrides,
        send_email: sendEmail,
      });
      toast.success(sendEmail ? "Vertrag erstellt und an Kunde gemailt" : "Vertrag erstellt");
      onDone(); onClose();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog onClose={onClose} title={`Vertrag generieren: ${template.name}`} wide>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#1A2433] mb-1">Kunde *</label>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#991B1B]/30 bg-white"
            data-testid="contract-user-select">
            <option value="">— bitte wählen —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name} ({u.email}){u.company ? ` — ${u.company}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-sm font-medium text-[#1A2433] mb-2">
            Platzhalter-Werte
            <span className="ml-2 text-xs font-normal text-slate-500">— leer lassen für Auto-Befüllung aus Kundendaten</span>
          </div>
          <div className="space-y-2">
            {(template.placeholders || []).map((p) => (
              <div key={p} className="grid grid-cols-[180px_1fr] gap-3 items-center">
                <label className="text-xs font-mono text-slate-600 truncate">[{p}]</label>
                <input
                  type="text"
                  value={overrides[p] || ""}
                  placeholder={defaultHintFor(p)}
                  onChange={(e) => setOverrides((o) => ({ ...o, [p]: e.target.value }))}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#991B1B]/30"
                  data-testid={`override-${p.replace(/[^a-z]/gi, "")}`}
                />
              </div>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
          <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="mt-0.5 accent-[#991B1B]" data-testid="send-email-checkbox" />
          <div className="text-sm">
            <div className="font-medium text-[#1A2433]">Direkt per E-Mail an Kunde senden</div>
            <div className="text-xs text-slate-500">PDF wird als Anhang von <code>offerten@mbw-swiss.ch</code> versendet (BCC: office@). Andernfalls können Sie nach dem Generieren manuell versenden.</div>
          </div>
        </label>
      </div>
      <DialogFooter>
        <button onClick={onClose} className="btn-secondary">Abbrechen</button>
        <button onClick={submit} disabled={busy || !userId} className="btn-primary" data-testid="generate-submit-btn">
          {busy ? "Erzeuge…" : (sendEmail ? "Generieren & senden" : "Generieren")}
        </button>
      </DialogFooter>
    </Dialog>
  );
}

function defaultHintFor(placeholder) {
  const map = {
    "Vorname Nachname / Firma": "Auto: Firma oder Vor- + Nachname",
    "Kunde": "Auto: Firma oder Vor- + Nachname",
    "Vorname": "Auto aus Kundendaten",
    "Nachname": "Auto aus Kundendaten",
    "Firma": "Auto aus Kundendaten",
    "Adresse": "Auto aus Intake (founder_address)",
    "Anschrift": "Auto aus Intake (founder_address)",
    "Strasse": "Auto aus Adresse (vor PLZ)",
    "PLZ": "Auto aus Adresse (4-5 Ziffern)",
    "Ort": "Auto aus Adresse (Stadt)",
    "Land": "Auto aus Intake (founder_country)",
    "Email": "Auto aus Kundendaten",
    "Telefon": "Auto aus Kundendaten",
    "Betrag": "Standard: 150.– pro Stunde nach effektivem Aufwand",
    "Honorar": "Standard: 150.– pro Stunde nach effektivem Aufwand",
    "Datum": "Auto: heute (TT.MM.JJJJ)",
    "Rechtsform": "Auto aus Intake (legal_form_target)",
    "Branche": "Auto aus Intake (business_sector)",
    "Geschäftszweck": "Auto aus Intake (company_purpose)",
  };
  return map[placeholder] || "Auto-Befüllung oder manuell überschreiben";
}

function Dialog({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-[10002] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-heading text-lg font-semibold text-[#1A2433]">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </header>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function DialogFooter({ children }) {
  return <div className="mt-6 pt-4 border-t border-slate-200 flex justify-end gap-2">{children}</div>;
}
